import React, { useEffect, useRef, useState } from 'react'
import { useFilter, Key } from 'react-aria'
import { graphql, useStaticQuery } from 'gatsby'
import { Button, ComboBox, Input, ListBox, ListBoxItem, Popover } from 'react-aria-components'
import ChevronDownBlack from '../../stylesheets/images/icons/icon-chevron-down.svg'
import { statesMap, abbrMap } from '../../../constants'
import * as E from '../../elementor'
import { useFlags } from 'gatsby-plugin-launchdarkly'
import { getReportingAdapter } from '../../reporting/get-reporting-adapter'
import { EventPrefix, Trigger } from '@pathccm/common-analytics/events'
import { freshpaintTrack } from '../../freshpaint'

const locationsArray = [
  {
    url: 'firstresponder',
    param: '?specialization=First+Responders'
  },
  {
    url: 'act-therapy',
    param: '?modality=Acceptance+and+Commitment+%28ACT%29'
  },
  {
    url: 'anxiety-therapy',
    param: '?specialization=Anxiety'
  },
  {
    url: 'adhd-therapy',
    param: '?specialization=ADHD'
  },
  {
    url: 'trauma-therapy',
    param: '?specialization=Trauma+and+PTSD'
  },
  {
    url: 'depression-therapy',
    param: '?specialization=Depression'
  },
  {
    url: 'emdr-therapy',
    param: '?modality=EMDR',
  },
  {
    url: 'cbt-therapy',
    param: '?modality=Cognitive+Behavioral+%28CBT%29'
  },
  {
    url: 'dbt-therapy',
    param: '?modality=Dialectical+%28DBT%29'
  }
]

interface DataItem {
  name: string,
  id: string
}

interface FormProps {
  textItems?: E.TestimonialWidget,
  textItemClass?: string
}

const PaidSocialForm: React.FC<FormProps> = ({ textItems, textItemClass }) => {
  if (typeof window === 'undefined') return <></>

  const isFirstResponder = window.location.href.includes('firstresponder')
  const result  = useStaticQuery(graphql`
      query {
        statesAndInsurances {
          statesServed
          insurancesServed
      }
    }
  `)


  const stateRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null)
  const insuranceRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null)
  const stateButton: React.MutableRefObject<HTMLButtonElement | null> = useRef(null)
  const insuranceButton: React.MutableRefObject<HTMLButtonElement | null> = useRef(null)

  const statesObj = result.statesAndInsurances.statesServed
  const insurancesObj = result.statesAndInsurances.insurancesServed
  const statesArray = Object.keys(statesObj).map(state => ({name: state, id: statesMap[state]}))
  const insurancesArray = [{name: 'I Don’t Plan to Use Insurance', id: 'I Don’t Plan to Use Insurance'}, ...Object.keys(insurancesObj).sort().map(insurance => ({name: insurance, id: insurance})), {name: 'I Don’t See Mine', id: 'I Don’t See Mine'}]

  const [statePopoverOpen, setStatePopoverOpen] = useState<boolean>(false);
  const [insurancePopoverOpen, setInsurancePopoverOpen] = useState<boolean>(false);
  const [stateInvalid, setStateInvalid] = useState<boolean>(false)
  const [insuranceInvalid, setInsuranceInvalid] = useState<boolean>(false)
  const [formLink, setFormLink] = useState<string>('https://rula.com/therapists')
  const [stateFilter, setStateFilter] = useState<string>(isFirstResponder ? 'Colorado' : '')
  const [insuranceFilter, setInsuranceFilter ] = useState<string>(isFirstResponder ? 'Anthem' : '')
  const [states, setStates] = useState<DataItem[]>(statesArray)
  const [insurances, setInsurances] = useState<DataItem[]>(insurancesArray)

  const { startsWith } = useFilter({ sensitivity: 'base' })

  const filterFunc = ({ array, filter}) => {
    return array.filter(item => startsWith(item.name, filter))
  }

  const { marketingSiteSegmentTransition } = useFlags()
  const reporting = getReportingAdapter(marketingSiteSegmentTransition)

  const stateChange = (key: Key | null) => {
    // If there is a selected insurance that is incompatible with the new state, clear the insurance input
    if (!!key) {
      const transformedKey = abbrMap[(key as string).toLowerCase()]
      const stateInsArray = statesObj[transformedKey]
      if (!!stateInsArray && !!insuranceFilter && !stateInsArray.includes(insuranceFilter)) setInsuranceFilter('')
    }
    trackFormEvent('StateSelect')
    setStatePopoverOpen(false)
    stateRef.current?.blur()
    stateButton.current?.blur()
  }

  const insuranceChange = (key: Key | null) => {
    // If there is a selected state that is incompatible with the new insurance, clear the state input
    if (!!key) {
      const insStatesArray: string[] | null = insurancesObj[key] ?? null
      if (!!insStatesArray && !!stateFilter && !insStatesArray.includes(stateFilter)) setStateFilter('')
    }
    trackFormEvent('InsuranceSelect');
    setInsurancePopoverOpen(false)
    insuranceRef.current?.blur()
    insuranceButton.current?.blur()
  }

  const trackFormEvent = (event_type: string) => {
    if (marketingSiteSegmentTransition) {
      reporting.track({
        eventName: {
          prefix: EventPrefix.MARKETING_SITE,
          trigger: Trigger.FORM_INTERACTION
        },
        properties: {
          form_name: 'embedded',
          interaction_type: event_type
        }
      })
    }
  }

  const infoItems = textItems ? textItems.testimonials : null
  const infoItemsToShow = infoItems?.length ?? 0

  const infoItemKeyframes = [
    {opacity: 0, display: 'flex', transform: 'translateY(-20%)'},
    {opacity: 1, transform: 'translateY(0%)', zIndex: '10', offset: '.05'},
    {opacity: 1, transform: 'translateY(0%)', zIndex: '10', offset: `${1/infoItemsToShow - .05}`},
    {opacity: 0, display: 'none', transform: 'translateY(20%)', zIndex: '1', offset: `${1/infoItemsToShow}`}
  ]

  const infoItemTiming = {
    duration: infoItemsToShow * 2500,
    iterations: Infinity,
  }

  useEffect(() => {
    const findStateID = (stateName: string) => {
      let stateId = ''
      states.forEach(state => {
        if (state.name === stateName) stateId = state.id
      })
      return stateId
    }

    const buildInsuranceParam = (ins: string) => {
      if (ins === 'Anthem') return 'Anthem+Blue+Cross'
      let insuranceId = ''
      insurances.forEach(insurance => {
        if (insurance.name === ins) insuranceId = insurance.name
      })
      return insuranceId.split(' ').join('+')
    }

    let formLinkUrl = 'https://rula.com/therapists'
    let locationParam = ''
    locationsArray.forEach(location => {
      if (!!locationParam) return
      if (window.location.href.includes(location.url)) return locationParam = location.param
    })
    if (!!stateFilter) formLinkUrl = formLinkUrl + '/' + findStateID(stateFilter)
    formLinkUrl = formLinkUrl + locationParam
    !!locationParam ? formLinkUrl = formLinkUrl + '&' : formLinkUrl = formLinkUrl + '?'
    if (!!insuranceFilter) formLinkUrl = formLinkUrl + 'insurance=' + buildInsuranceParam(insuranceFilter)
    setFormLink(formLinkUrl)
  }, [stateFilter, insuranceFilter])

  // Animation - need JS because we need to know how many items there are to determine timing. See above
  useEffect(() => {
    const animatedItems = document.getElementsByClassName(textItemClass ?? 'text-item')
    const tooltipItem = document.querySelector(`.tooltip.${textItemClass}`)
    Array.prototype.forEach.call(
      animatedItems,
      (item, index) => {
        const animation = item.animate(infoItemKeyframes, {...infoItemTiming, delay: 2500 * index})
        // pause all animations when tooltip is hovered, resume when mouse-out
        tooltipItem?.addEventListener('mouseover', () => animation.pause())
        tooltipItem?.addEventListener('mouseout', () => animation.play())
      }
    )
  }, [])

  // Track state input filter change
  useEffect(() => {
    setStateInvalid(false)
    const newStates = filterFunc({ array: statesArray, filter: stateFilter })
    // If the state input matches a selectable state submit the stateChange automatically
    if (newStates[0]?.name.toLowerCase() === stateFilter.toLowerCase()) {
      stateChange(newStates[0].name)
      setStateFilter(newStates[0].name)
    }
    setStates(newStates)
  }, [stateFilter])

  // Track insurance input filter change
  useEffect(() => {
    setInsuranceInvalid(false)
    const newInsurances = filterFunc({ array: insurancesArray, filter: insuranceFilter })
    // If the insurance input matches a selectable insurance submit the insuranceChange automatically
    // Check for newInsurances length as some insurances start with the same name (eg. Aetna/Aetna - Medicare Advantage)
    if (newInsurances.length == 1 && newInsurances[0]?.name.toLowerCase() === insuranceFilter.toLowerCase()) {
      insuranceChange(newInsurances[0].name)
      setInsuranceFilter(newInsurances[0].name)
    }
    setInsurances(newInsurances)
  }, [insuranceFilter])

  const handleInputClick = (eventName: 'StateInput' | 'InsuranceInput') => {
    trackFormEvent(eventName)
    if (eventName === 'StateInput') {
      stateButton.current?.click()
      setStatePopoverOpen(true)
      setStates(statesArray)
    } else {
      insuranceButton.current?.click()
      setInsurancePopoverOpen(true)
      return setInsurances(insurancesArray)
    }
  }

  const checkInvalid = () => {
    trackFormEvent('DirectoryFormSubmit')
    if (!stateFilter) setStateInvalid(true)
    if (!insuranceFilter) setInsuranceInvalid(true)
    if (!!stateFilter && !!insuranceFilter) {
      freshpaintTrack('DirectoryFormSubmit')
      return window.open(formLink)
    }
  }

  return (
    <form className='paid-social-form' name='embedded-2f2f' id='embedded-2f2f-form'>
      <label>State</label>
      <ComboBox onBlur={() => setStatePopoverOpen(false)} isInvalid={stateInvalid} isRequired={true} name='state' aria-label='State' items={states} onInputChange={setStateFilter} inputValue={stateFilter} onSelectionChange={stateChange}>
        <Input ref={stateRef} className='react-aria-Input state' placeholder='Type or select your state...' onFocus={() => handleInputClick('StateInput')} />
        <Button ref={stateButton} onPress={() => trackFormEvent('StateDropdown')}>
          <img src={ChevronDownBlack} alt='Arrow' />
        </Button>
        <Popover placement='bottom' offset={8} isOpen={statePopoverOpen}>
          <ListBox>
            {(state: {name: string, id: string}) => <ListBoxItem>{state.name}</ListBoxItem>}
          </ListBox>
        </Popover>
      </ComboBox>
      {stateInvalid && <p className='errorMessage'>Please select a state</p>}
      <label>Insurance</label>
      <ComboBox onBlur={() => setInsurancePopoverOpen(false)} isInvalid={insuranceInvalid} isRequired={true} name='insurance' aria-label='Insurance selection' items={insurances} onInputChange={setInsuranceFilter} inputValue={insuranceFilter} onSelectionChange={insuranceChange}>
        <Input ref={insuranceRef} className='react-aria-Input insurance' placeholder='Type or select an insurance...' onFocus={() => handleInputClick('InsuranceInput')} />
        <Button ref={insuranceButton} onPress={() => trackFormEvent('InsuranceDropdown')}>
          <img src={ChevronDownBlack} alt='Arrow' />
        </Button>
        <Popover placement='bottom' offset={8} isOpen={insurancePopoverOpen}>
          <ListBox>
            {(insurance: {name: string, id: string}) => <ListBoxItem>{insurance.name}</ListBoxItem>}
          </ListBox>
        </Popover>
      </ComboBox>
      {insuranceInvalid && <p className='errorMessage'>Please select an insurance carrier</p>}
      <button id='directory-form-submit' className='rula-button amethist' type='submit' onClick={checkInvalid}>Browse providers</button>
    </form>
  )
}

export default PaidSocialForm
